import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import PDFfilePreview from "../../util/PDFfilePreview";
import API from "../../util/api";
import { Container, Button, Box, Typography } from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import SaveIcon from "@material-ui/icons/Save";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import ApiError from "../../Error/ApiError";

import { useReceiptStyles } from "./receiptStyles";
import BottomNavButton from "../Navigation/BottomNavButton";

import { useFormik } from "formik";
import * as yup from "yup";
import {
  selectSelectedReceipt,
  setSelectedReceiptFile,
  updateReceiptFile,
} from "../../slices/receiptSlice";
import { useSelector } from "react-redux";
import { createPDFfromResponse } from "../../util/pdfblob";
import { setNavBarActionStatus } from "../Navigation/navSlice";
import AlertDialog from "../../Dialogs/AlertDialog";
import { setAlertDialogVisible } from "../../Dialogs/confirmDialogSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import { useAppDispatch } from "../../app/store";
import LoadingScreen from "../../LoadingScreen";

const validationSchema = yup.object({
  file: yup.string(),
});

//File size max limit (bytes)
const FILE_SIZE_LIMIT = 100000000;

/**
 * Upload one file view
 *
 */
const ReceiptFileUpload = () => {
  let { editMode } = useParams<any>(); //EditMode = true
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const selectedReceipt = useSelector(selectSelectedReceipt || null);
  const [files, setFiles] = useState<File[]>([]);
  const [fileName, setFilename] = useState("Lataa tiedosto(t)");
  const [previewFilePath, setPreviewFilePath] = useState<any>(
    !editMode ? selectedReceipt.file : "" || ""
  );
  let history = useHistory();
  const classes = useReceiptStyles();
  // const dispatch = useDispatch();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(setNavBarActionStatus("closeAndConfirm"));
  }, []);

  // Get the file stream from current state always before componen render
  // if not set
  useEffect(() => {
    if (selectedReceipt.file !== "" && !editMode) {
      // if (!editMode) dispatch(clearSelectedReceipt());
      API.getConvertedPDF(selectedReceipt.file).then((response: any) => {
        const receiptPdfUrl = createPDFfromResponse(response.data);
        setPreviewFilePath(receiptPdfUrl);
      });
    } else {
      setPreviewFilePath("");
      setFiles([]);
    }
  }, []);

  useEffect(() => {
    //Convert if files are present and all are under 100 mb in size
    if (files.length > 0) {
      const oversizedFiles = files.filter(f => f.size > FILE_SIZE_LIMIT);
      if (oversizedFiles.length > 0) {
        setApiError(
          `Jotkut tiedostot ovat liian suuria. Max. sallittu tiedostokoko on ${
            FILE_SIZE_LIMIT / 1000000
          }mb`
        );
      } else {
        handleSelectedFiles();
      }
    }
  }, [files]);

  const formik = useFormik({
    initialValues: {
      file: null,
    },
    validationSchema: validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      //console.log("No submit, change this to continue or something to");
    },
  });

  //Convert selected files to pdf and return pdf preview
  const handleSelectedFiles = () => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("receiptFiles[]", file);
    });
    setLoading(true);
    API.convertToPDF(formData)
      .then((response: any) => {
        //If conversion is succesfull
        if (response.data.status === "success") {
          //Get the converted preview file as binarystream and
          //set it as an value for the preview component
          dispatch(setSelectedReceiptFile(response.data.data.fileName));
          API.getConvertedPDF(response.data.data.fileName)
            .then((response: any) => {

              const receiptPdfUrl = createPDFfromResponse(response.data);
              setPreviewFilePath(receiptPdfUrl);
            })
            .catch((_error) => {
              setApiError("Esikatselun luonti epäonnistui");
              setLoading(false);
            });
          setApiError("");
        } else if (response.data.status === "failed") {
          setApiError(response.data.message);
          setLoading(false);
        }
        setLoading(false);
      })
      .catch((_error) => {
        setApiError("Tiedostojen lataus epäonnistui");
        setLoading(false);
      });
    setFiles([]);
  };

  const handleCloseReceiptForm = () => {
    if (editMode) {
      history.push(`/customer/receipt/details/edit/${selectedReceipt.id}`);
    } else {
      history.push("/customer");
      //Needs to know exact file to remove if with same user
      /*       API.clearUserTmpFolder().then((response: any) => {
        // console.log("User tmp cleared?");
      }); */
    }
    dispatch(setAlertDialogVisible(false));
  };

  // When in edit mode, send request to update receipt's file
  const handleUpdateFile = () => {
    dispatch(
      updateReceiptFile({
        file: { file: selectedReceipt.file },
        id: selectedReceipt.id as string,
      })
    )
      .then(unwrapResult)
      .then((originalPromise: any) => {
        if (originalPromise.status === "failed") {
          setApiError(originalPromise.message);
        } else if (originalPromise.status === "success") {
          dispatch(setSelectedReceiptFile(originalPromise.data.file_url));
          history.push(`/customer/receipt/details/edit`);
        }
      });
  };

  // Navigate to receipt details where analysis will be performed
  const handleContinue = () => {
    if (!selectedReceipt.file) {
      return;
    }
    history.push('/customer/receipt/details');
  };

  return (
    <>
      <AlertDialog
        title="Peruuta muutokset"
        message="Haluatko peruutta tositteen luomisen?"
        handleCancel={() => dispatch(setAlertDialogVisible(false))}
        handleContinue={() => handleCloseReceiptForm()}
      />
      <Container>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          className={classes.fileUploadContainer}
        >
          <Box>
            {previewFilePath.length > 0 ? (
              <PDFfilePreview pageWidth={200} filePath={previewFilePath} />
            ) : null}
          </Box>
          
          {previewFilePath.length === 0 && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                borderRadius: "12px",
                padding: "32px 24px",
                marginTop: "16px",
                marginBottom: "16px",
                textAlign: "center",
              }}
            >
              <CloudUploadIcon style={{ fontSize: 45, color: "white", marginBottom: "16px" }} />
              <Typography variant="body1" style={{ fontWeight: "bold", marginBottom: "8px", color: "white" }}>
                Tuetut tiedostotyypit:
              </Typography>
              <Typography variant="body2" style={{ color: "white" }}>
                DOC, DOCX, XLS, XLSX, ODT, ODS, PDF, JPG, PNG
              </Typography>
              <Typography variant="body2" style={{ color: "white", marginTop: "8px" }}>
                Max. tiedostokoko 100mb
              </Typography>
            </Box>
          )}

          <form onSubmit={formik.handleSubmit} style={{ width: "100%" }}>
            <Button
              variant="contained"
              color="primary"
              component="label"
              fullWidth
              size="large"
            >
              {selectedReceipt.file !== "" ? "Vaihda tiedostot" : "Valitse tiedostoja"}
              <input
                type="file"
                accept=".doc,.docx,.xls,.xlsx,.pdf,.jpg,.png,application/vnd.oasis.opendocument.text,application/vnd.oasis.opendocument.spreadsheet"
                multiple
                hidden
                onChange={(event: any) => {
                  const selectedFiles = Array.from(event.target.files || []) as File[];
                  if (selectedFiles.length > 0) {
                    formik.setFieldValue("file", selectedFiles);
                    setFiles(selectedFiles);
                    if (selectedFiles.length === 1) {
                      setFilename(selectedFiles[0].name);
                    } else {
                      setFilename(`${selectedFiles.length} tiedostoa valittu`);
                    }
                  }
                }}
              />
            </Button>
          </form>
          
          <ApiError
            message={apiError}
            show={apiError.length > 0 ? true : false}
          />
        </Box>
      </Container>
      <BottomNavButton
        fixed={true}
        disabled={selectedReceipt.file !== "" ? false : true}
        text={editMode ? "Tallenna" : "Jatka"}
        onClick={
          editMode
            ? () => handleUpdateFile()
            : () => handleContinue()
        }
        fabIcon={editMode ? <SaveIcon /> : <ArrowForwardIcon />}
        fabOnClick={
          editMode
            ? () => handleUpdateFile()
            : () => handleContinue()
        }
      />
      <LoadingScreen
        loading={loading}
        loadingText="Ladataan tiedostoja... Odota hetki..."
      />
    </>
  );
};
export default ReceiptFileUpload;
